import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import Image from '../Image/Image';
import TextCopiedChip from '../TextCopiedChip/TextCopiedChip';
import videoIcon from '../../assets/images/video-camera-solid.svg';
import phoneIcon from '../../assets/images/phone-solid.svg';
import copyIcon from '../../assets/images/clipboard-outline.svg';
import { copyTextToClipboard } from '../../helper/commonHelper';
import {
  VIDEO_INTERPRETER_JOINED,
  AUDIO_INTERPRETER_JOINED,
  JOIN_TIME,
  INTERPRETER_NAME,
  INTERPRETER_ID,
  INTERPRETER_VIDEO_ICON,
  INTERPRETER_AUDIO_ICON,
  INTERPRETER_VIDEO,
  INTERPRETER_AUDIO,
  POLL_FOR_INTERPRETER_VIDEO,
  POLL_FOR_INTERPRETER_AUDIO,
  COPIED_TO_CLIPBOARD,
  COPY_NAME,
  ID_TO_CLIPBOARD,
  COPIED_TEXT_TIMEOUT,
  INTERPRETOR_POLLING_DEFAULT_INTERVAL,
  EVENT_ID_VIDEO_JOIN,
  EVENT_ID_AUDIO_JOIN
} from '../../constants/interpreter.constants';
import { postInterpreterPoll } from '../../service/interpreter.service';
import InterpreterAction from '../../store/actions/InterpreterAction';
import { STORE_NAMES } from '../../constants/store.constants';
import { formatTimestamp } from '../../helper/commonUtils';

const InterpreterJoinedList = ({ region, zoomMeetingId, interpreterType, compact }) => {
  // Track last sessionID to detect new invites
  const lastSessionIDRef = useRef(null);
  // Track last tab type so stop logic does not carry over across tab switches
  const lastInterpreterTypeRef = useRef(null);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [lastPollTm, setLastPollTm] = useState('');
  const lastPollTmRef = useRef('');
  const [pollingActive, setPollingActive] = useState(false);
  const pollingIntervalRef = useRef(null);

  const dispatch = useDispatch();
  const { sessionID } = useSelector(state => state?.[STORE_NAMES.INVITE_BUTTON] || {});
  const interpreterEventsPolling = useSelector(state => state?.[STORE_NAMES.APP]?.interpreterEventsPolling);

  // Select correct lists from Redux
  const videoJoinedInterpretersList = useSelector(state => state?.[STORE_NAMES.INTERPRETER]?.videoJoinedInterpretersList || []);
  const audioJoinedInterpretersList = useSelector(state => state?.[STORE_NAMES.INTERPRETER]?.audioJoinedInterpretersList || []);

  // Choose which list to show
  const joinedInterpretersList = interpreterType === INTERPRETER_VIDEO ? videoJoinedInterpretersList : audioJoinedInterpretersList;

  // Get region config
  const { enabled = false, pollingInterval = INTERPRETOR_POLLING_DEFAULT_INTERVAL, regions = {} } = interpreterEventsPolling || {};
  const regionConfig = regions[region];
  const pollForType = interpreterType === INTERPRETER_VIDEO ? POLL_FOR_INTERPRETER_VIDEO : POLL_FOR_INTERPRETER_AUDIO;
  const pollForIndex = regionConfig?.pollFor?.indexOf(pollForType) ?? -1;
  const stopPollEvent = pollForIndex >= 0 ? regionConfig?.stopPollfor?.[pollForIndex] : undefined;

  // Should polling start for this region/type?
  const shouldStartPolling = enabled && Boolean(regionConfig?.pollFor?.includes(pollForType));

  // Keep the ref in sync with state
  useEffect(() => {
    lastPollTmRef.current = lastPollTm;
  }, [lastPollTm]);

  useEffect(() => {
    // Only start polling if zoomMeetingId exists
    if (!zoomMeetingId) return;

    // Reset stop-check state when switching between audio/video tabs
    if (lastInterpreterTypeRef.current !== interpreterType) {
      lastInterpreterTypeRef.current = interpreterType;
    }

    // Detect new interpreter invite by sessionID change
    if (lastSessionIDRef.current !== sessionID) {
      // New invite detected, restart polling
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setPollingActive(false);
      setLastPollTm('');
      lastPollTmRef.current = '';
      lastSessionIDRef.current = sessionID;
    }

    const fetchAndDispatchInterpreters = async () => {
      try {
        const payload = {
          region,
          zoomMeetingId,
          sessionId: sessionID,
          lastPollTm: '' // Always fetch all events every poll
        };
        const response = await postInterpreterPoll(payload);
        const pollEvents = Array.isArray(response) ? response : Array.isArray(response?.events) ? response.events : [];
        // Filter events for this meeting and by event type
        let videoEvents = [];
        let audioEvents = [];
        let meetingEvents = [];

        if (shouldStartPolling && pollEvents.length > 0) {
          meetingEvents = pollEvents.filter(event => event.vmrID === zoomMeetingId);
          if (interpreterType === INTERPRETER_VIDEO) {
            videoEvents = meetingEvents.filter(event => event.eventID === stopPollEvent);
          } else if (interpreterType === INTERPRETER_AUDIO) {
            audioEvents = meetingEvents.filter(event => event.eventID === stopPollEvent);
          }
        }
        // Update both lists with filtered events
        dispatch(InterpreterAction.setVideoJoinedInterpreterList(videoEvents));
        dispatch(InterpreterAction.setAudioJoinedInterpreterList(audioEvents));

        // Update lastPollTm for next poll
        if (response && response.eventsTo) {
          setLastPollTm(response.eventsTo);
        }

        // REGION-WISE STOP POLLING LOGIC
        // Stop only if we get a VIDEO or AUDIO JOIN event for this meeting and active invite session.
        const stopEventID = interpreterType === INTERPRETER_VIDEO ? EVENT_ID_VIDEO_JOIN : EVENT_ID_AUDIO_JOIN;
        const shouldStopForJoinEvent = Boolean(sessionID) && meetingEvents.some(event => event.eventID === stopEventID && event.sessionID === sessionID);

        const shouldStop = shouldStopForJoinEvent;

        if (shouldStop && pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setPollingActive(false);
        }
      } catch (error) {
        console.error('Error fetching joined interpreters:', error);
      }
    };

    if (shouldStartPolling) {
      setLastPollTm('');
      setPollingActive(true);
      fetchAndDispatchInterpreters();
      pollingIntervalRef.current = setInterval(fetchAndDispatchInterpreters, pollingInterval);
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setPollingActive(false);
    };
  }, [sessionID, region, zoomMeetingId, interpreterType, shouldStartPolling, pollingInterval]);

  // Sort all interpreters by timestamp descending (most recent first)
  const sortedInterpreters = Array.isArray(joinedInterpretersList)
    ? [...joinedInterpretersList].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    : [];

  // Only render if there is data for the selected tab
  if (!sortedInterpreters.length) {
    return null;
  }

  const handleCopy = (name, id, idx) => {
    let textToCopy = id && id.trim() !== '' ? `${name}, ${id}` : name;
    copyTextToClipboard(textToCopy);
    setCopiedIdx(idx);
    setTimeout(() => {
      setCopiedIdx(null);
    }, COPIED_TEXT_TIMEOUT);
  };

  return (
    <div className={`interpreter-joined-list ${compact ? 'compact' : ''}`}>
      {sortedInterpreters.map((interpreter, idx) => {
        const { timestamp, interpreterName, interpreterID } = interpreter;
        return (
          <div className="interpreter-joined-header" key={idx}>
            <div className="interpreter-joined-item">
              <span className="video-icon-container">
                <Image
                  src={interpreterType === INTERPRETER_VIDEO ? videoIcon : phoneIcon}
                  alt={interpreterType === INTERPRETER_VIDEO ? INTERPRETER_VIDEO_ICON : INTERPRETER_AUDIO_ICON}
                  className={interpreterType === INTERPRETER_VIDEO ? 'video-icon' : 'audio-icon'}
                />
              </span>
              <span className="interpreter-joined">
                {' '}
                {interpreterType === INTERPRETER_VIDEO ? VIDEO_INTERPRETER_JOINED : AUDIO_INTERPRETER_JOINED}
              </span>
            </div>
            <ul className="interpreter-info-list">
              <li>
                <span className="info-label">{JOIN_TIME}</span> <span className="info-value">{formatTimestamp(timestamp)}</span>
              </li>
              <li>
                <span className="info-label">{INTERPRETER_NAME}</span> <span className="info-value">{interpreterName}</span>
              </li>
              <li>
                <span className="info-label">{INTERPRETER_ID}</span> <span className="info-value">{interpreterID}</span>
              </li>
            </ul>
            <div className="copy-row">
              <div onClick={() => handleCopy(interpreterName, interpreterID, idx)}>
                <span>
                  {COPY_NAME} &amp; {ID_TO_CLIPBOARD}
                </span>
                <Image src={copyIcon} alt="copy icon" className="copy-icon" />
                <TextCopiedChip copiedStatus={copiedIdx === idx} label={COPIED_TO_CLIPBOARD} customStyle={'vve-interpreter-only'} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

InterpreterJoinedList.propTypes = {
  region: PropTypes.string.isRequired,
  zoomMeetingId: PropTypes.string.isRequired,
  interpreterType: PropTypes.string.isRequired,
  compact: PropTypes.bool
};

export default InterpreterJoinedList;
